import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

/**
 * Measure whether one-line text (nowrap + ellipsis) actually overflows.
 * When clamping is off (expanded), keep the last overflowing=true so "more" stays.
 */
@Directive({
  selector: '[wallLineOverflow]',
})
export class WallLineOverflowDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() wallLineOverflow = true;
  @Output() wallLineOverflowing = new EventEmitter<boolean>();

  private overflow: boolean | null = null;
  private resizeObs: ResizeObserver | null = null;
  private mutationObs: MutationObserver | null = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.queueMeasure());
      this.resizeObs.observe(el);
    }
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObs = new MutationObserver(() => this.queueMeasure());
      this.mutationObs.observe(el, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
    this.queueMeasure();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wallLineOverflow']) {
      this.queueMeasure();
    }
  }

  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
    this.mutationObs?.disconnect();
  }

  private queueMeasure(): void {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.measure());
    });
  }

  private measure(): void {
    if (!this.wallLineOverflow) {
      return;
    }
    const el = this.host.nativeElement;
    const overflowing = el.scrollWidth - el.clientWidth > 1;
    this.emitOverflow(overflowing);
  }

  private emitOverflow(next: boolean): void {
    if (this.overflow === next) {
      return;
    }
    this.overflow = next;
    this.ngZone.run(() => this.wallLineOverflowing.emit(next));
  }
}
